import { useEffect, useState } from 'react';
import { api } from '../api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = () => api.getMessages().then(setMessages).catch((e) => setError(e.message));

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []);

  const filtered = messages.filter((m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      m.name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      m.subject.toLowerCase().includes(q) ||
      m.message.toLowerCase().includes(q)
    );
  });

  const openMessage = async (msg) => {
    setSelected(msg);
    if (msg.status === 'Unread') {
      try {
        const updated = await api.markMessageRead(msg.id);
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? updated : m)));
        setSelected(updated);
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const unreadCount = messages.filter((m) => m.status === 'Unread').length;

  return (
    <div>
      <PageHeader
        title="Contact Messages"
        subtitle="Messages sent from the LUMORA website contact form"
      />
      {error && <p className="text-red-600 mb-4">{error}</p>}

      <div className="glass-card mb-6 flex flex-wrap items-center gap-4">
        <input
          type="search"
          placeholder="Search name, email, subject..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input flex-1 min-w-[200px]"
        />
        <span className="text-sm text-lumora-text/60">
          {unreadCount > 0 ? (
            <span className="text-lumora-wood font-medium">{unreadCount} unread</span>
          ) : (
            'All caught up'
          )}
        </span>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 min-h-[480px]">
        <div className="lg:col-span-2 glass-card !cursor-default hover:shadow-soft p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-lumora-secondary/80 bg-lumora-secondary/30">
            <h3 className="font-serif text-sm font-medium text-lumora-dark-wood">Inbox</h3>
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-lumora-secondary/60">
            {filtered.map((msg) => (
              <button
                key={msg.id}
                type="button"
                onClick={() => openMessage(msg)}
                className={`w-full text-left px-4 py-3 transition-colors hover:bg-lumora-secondary/40 ${
                  selected?.id === msg.id ? 'bg-lumora-secondary/50' : ''
                } ${msg.status === 'Unread' ? 'border-l-4 border-lumora-wood' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm truncate ${msg.status === 'Unread' ? 'font-semibold text-lumora-dark-wood' : 'text-lumora-text'}`}>
                    {msg.name}
                  </p>
                  <StatusBadge status={msg.status} />
                </div>
                <p className="text-xs text-lumora-text/70 truncate mt-0.5">{msg.subject}</p>
                <p className="text-xs text-lumora-text/40 mt-1">{msg.date} · {msg.time}</p>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-lumora-text/50 py-12 text-sm">No messages yet</p>
            )}
          </div>
        </div>

        <div className="lg:col-span-3 glass-card !cursor-default hover:shadow-soft">
          {selected ? (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-6 pb-4 border-b border-lumora-secondary/80">
                <div>
                  <h3 className="font-serif text-xl text-lumora-dark-wood">{selected.subject}</h3>
                  <p className="text-sm text-lumora-text/60 mt-1">
                    From <span className="text-lumora-wood">{selected.name}</span>
                  </p>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6 text-sm">
                <div className="bg-lumora-secondary/30 rounded-xl px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-lumora-text/50 mb-1">Email</p>
                  <a href={`mailto:${selected.email}`} className="text-lumora-wood hover:underline">
                    {selected.email}
                  </a>
                </div>
                <div className="bg-lumora-secondary/30 rounded-xl px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-lumora-text/50 mb-1">Received</p>
                  <p>{selected.date} at {selected.time}</p>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-lumora-text/50 mb-2">Message</p>
                <div className="bg-lumora-white/50 border border-lumora-secondary rounded-xl p-5 text-sm text-lumora-text leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[360px] text-lumora-text/40">
              <span className="text-4xl mb-3 opacity-50">✉</span>
              <p className="text-sm">Select a message to read</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
